import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Zap, Target, TrendingUp, ShieldCheck, Sparkles, BarChart3, Users,
  Play, Check, Star, ArrowRight, Eye, Heart, Music2, Instagram, Youtube, Facebook,
} from "lucide-react";

const miniData = [12, 18, 16, 28, 34, 30, 46, 58, 55, 72, 88, 96].map((v, i) => ({ i, v }));

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const steps = [
  { icon: Users, title: "Connect your handle", desc: "Add your TikTok @handle. No password, ever. We work fully outside your account." },
  { icon: Target, title: "Define your audience", desc: "Pick your niche, interests, age, location and language so we reach the right viewers." },
  { icon: Zap, title: "AI promotes your posts", desc: "Our engine surfaces your content to targeted, real users primed to follow and engage." },
  { icon: BarChart3, title: "Track real growth", desc: "Watch followers, views and engagement climb on your live analytics dashboard." },
];

const features = [
  { icon: Target, span: "md:col-span-7", title: "Laser audience targeting", desc: "Filter by niche, hashtags, geography and even competitor audiences to find fans who actually care about your content." },
  { icon: ShieldCheck, span: "md:col-span-5", title: "Password-free & safe", desc: "We never ask for your login. 100% TOS-friendly organic promotion." },
  { icon: Sparkles, span: "md:col-span-5", title: "Trend-aware AI", desc: "Models trained on live trends surface your posts when momentum is highest." },
  { icon: TrendingUp, span: "md:col-span-7", title: "Compounding organic growth", desc: "Real followers that keep engaging — driving the algorithm to push you further, for free." },
];

const plans = [
  { id: "starter", name: "Starter", price: 29, tagline: "Kickstart your presence", featured: false,
    features: ["1,200+ followers / mo", "35K+ organic views / mo", "Audience targeting", "Weekly growth report"] },
  { id: "growth", name: "Growth", price: 59, tagline: "Scale fast with momentum", featured: true,
    features: ["3,000+ followers / mo", "120K+ organic views / mo", "Advanced niche targeting", "Dedicated specialist", "Priority chat support"] },
  { id: "pro", name: "Pro", price: 99, tagline: "For creators going viral", featured: false,
    features: ["6,500+ followers / mo", "320K+ organic views / mo", "Manual targeting reviews", "Influencer exposure", "24/7 dedicated manager"] },
];

const reviews = [
  { name: "Maya R.", handle: "@mayacreates", text: "Went from 2K to 19K in six weeks. The targeting is unreal — my new followers actually watch and comment.", up: "+17K" },
  { name: "Deshawn K.", handle: "@dksounds", text: "Finally growth that isn't bots. My engagement rate doubled and two videos hit the FYP hard.", up: "+42K" },
  { name: "Lena P.", handle: "@lenaeats", text: "The dashboard keeps me obsessed. Seeing daily follower gains climb is addictive. Worth every dollar.", up: "+9.4K" },
];

const StatPill = ({ value, label }) => (
  <div className="text-center">
    <div className="font-heading text-3xl sm:text-4xl font-black text-primary">{value}</div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6">
        <div className="absolute inset-0 grid-glow opacity-40 pointer-events-none" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[130px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div initial="hidden" animate="show" variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-xs font-semibold uppercase tracking-widest text-primary mb-6">
              <Sparkles className="w-3.5 h-3.5" /> AI-powered organic growth
            </motion.div>
            <motion.h1 custom={1} initial="hidden" animate="show" variants={fadeUp} className="font-heading text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95]">
              Blow up on <span className="text-primary">TikTok</span>.<br /> Grow followers that <span className="text-secondary">actually stick</span>.
            </motion.h1>
            <motion.p custom={2} initial="hidden" animate="show" variants={fadeUp} className="mt-6 text-lg text-muted-foreground max-w-lg font-light leading-relaxed">
              CloutRise promotes your content to a laser-targeted audience of real people — no bots, no passwords. Watch your following climb on a live dashboard.
            </motion.p>
            <motion.div custom={3} initial="hidden" animate="show" variants={fadeUp} className="mt-8 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/register")} className="rounded-full h-12 px-8 bg-primary text-background font-bold text-base hover:bg-primary/90 hover:scale-105 transition-transform" data-testid="hero-cta-button">
                Start Growing Free <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full h-12 px-8 border-white/20 hover:border-white/50 bg-transparent text-base" data-testid="hero-how-button">
                <Play className="w-4 h-4 mr-1" /> How it works
              </Button>
            </motion.div>
            <motion.div custom={4} initial="hidden" animate="show" variants={fadeUp} className="mt-12 flex gap-10">
              <StatPill value="4.2M+" label="Followers gained" />
              <StatPill value="12K+" label="Creators" />
              <StatPill value="4.8★" label="Avg. rating" />
            </motion.div>
            <motion.div custom={5} initial="hidden" animate="show" variants={fadeUp} className="mt-8 flex items-center gap-5 text-muted-foreground" data-testid="platforms-strip">
              <span className="text-xs uppercase tracking-widest">Now growing</span>
              {[Music2, Instagram, Youtube, Facebook].map((Ic, i) => (
                <Ic key={i} className="w-5 h-5 hover:text-foreground transition-colors" />
              ))}
            </motion.div>
          </div>

          {/* Floating dashboard cards */}
          <div className="relative h-[460px] hidden lg:block">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="absolute top-0 right-0 w-[340px] rounded-2xl border border-primary/30 bg-card p-6 shadow-[0_0_40px_rgba(0,242,254,0.15)] animate-float">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Followers</span>
                <span className="text-xs font-bold text-primary flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +38%</span>
              </div>
              <div className="font-heading text-4xl font-black">142,908</div>
              <div className="h-24 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={miniData}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00F2FE" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#00F2FE" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="v" stroke="#00F2FE" strokeWidth={2.5} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="absolute bottom-6 left-0 w-[260px] rounded-2xl border border-white/10 bg-card p-5 shadow-xl" style={{ animation: "float 6s ease-in-out infinite", animationDelay: "1.5s" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center"><Eye className="w-5 h-5 text-secondary" /></div>
                <div>
                  <div className="text-xs text-muted-foreground">Views this week</div>
                  <div className="font-heading text-xl font-bold">1.2M</div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="absolute bottom-32 right-8 w-[210px] rounded-2xl border border-white/10 bg-card p-5 shadow-xl" style={{ animation: "float 6s ease-in-out infinite", animationDelay: "0.8s" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center"><Heart className="w-5 h-5 text-primary" fill="currentColor" /></div>
                <div>
                  <div className="text-xs text-muted-foreground">Engagement</div>
                  <div className="font-heading text-xl font-bold">9.4%</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">How it works</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Four steps to real momentum</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div key={s.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="rounded-2xl border border-border/50 bg-card p-7 hover:-translate-y-1 transition-transform duration-300" data-testid={`step-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5">
                  <s.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="font-mono text-xs text-muted-foreground mb-2">0{i + 1}</div>
                <h3 className="font-heading text-lg font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Why CloutRise</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Growth engineered for the algorithm</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {features.map((f, i) => (
              <motion.div key={f.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className={`${f.span} rounded-2xl border border-border/50 bg-card p-8 hover:border-primary/30 transition-colors`} data-testid={`feature-${i}`}>
                <div className="w-11 h-11 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center mb-5">
                  <f.icon className="w-5 h-5 text-secondary" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2">{f.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Pricing</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Plans that scale with you</h2>
            <p className="mt-4 text-muted-foreground">Cancel anytime. 7-day money-back guarantee on every plan.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {plans.map((p, i) => (
              <motion.div key={p.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className={`rounded-2xl p-8 border ${p.featured ? "border-primary/40 bg-card shadow-[0_0_30px_rgba(0,242,254,0.12)] md:-translate-y-3" : "border-border/50 bg-card"}`} data-testid={`pricing-${p.id}`}>
                {p.featured && <div className="inline-block px-3 py-1 rounded-full bg-primary text-background text-xs font-bold uppercase tracking-wider mb-4">Most popular</div>}
                <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
                <p className="text-sm text-muted-foreground mb-5">{p.tagline}</p>
                <div className="flex items-end gap-1 mb-6">
                  <span className="font-heading text-5xl font-black">${p.price}</span>
                  <span className="text-muted-foreground mb-1.5">/mo</span>
                </div>
                <Button onClick={() => navigate("/register")} className={`w-full rounded-full font-bold ${p.featured ? "bg-primary text-background hover:bg-primary/90" : "bg-white/5 text-foreground border border-white/15 hover:bg-white/10"} hover:scale-[1.02] transition-transform`} data-testid={`pricing-cta-${p.id}`}>
                  Get {p.name}
                </Button>
                <ul className="mt-7 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Creators love it</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Real creators. Real numbers.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <motion.div key={r.name} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="rounded-2xl border border-border/50 bg-card p-7" data-testid={`review-${i}`}>
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-primary" fill="currentColor" />)}</div>
                <p className="text-muted-foreground leading-relaxed mb-6">"{r.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.handle}</div>
                  </div>
                  <div className="font-heading font-black text-secondary">{r.up}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto rounded-3xl border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <h2 className="relative font-heading text-4xl sm:text-5xl font-black tracking-tight">Your next viral moment starts today</h2>
          <p className="relative mt-4 text-muted-foreground max-w-xl mx-auto">Join thousands of creators growing on autopilot. Set up in under two minutes.</p>
          <Button onClick={() => navigate("/register")} className="relative mt-8 rounded-full h-12 px-10 bg-primary text-background font-bold text-base hover:bg-primary/90 hover:scale-105 transition-transform" data-testid="final-cta-button">
            Start Growing Free <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
